/** Float-panel message list scroll — survives Turbo permanent DOM relocate (no Preact remount). */

const MAX_AGE_MS = 30 * 60 * 1000
const NEAR_BOTTOM_PX = 120

/** @type {Map<string, { scrollTop: number, pinned: boolean, savedAt: number }>} */
const memory = new Map()

/** @type {Map<string, () => HTMLElement | null>} */
const liveLists = new Map()

function isNearBottom(list) {
  if (!list) return false
  if (list.scrollHeight <= list.clientHeight) return true
  return list.scrollTop + list.clientHeight >= list.scrollHeight - NEAR_BOTTOM_PX
}

/**
 * @param {string} key
 * @returns {{ scrollTop: number, pinned: boolean } | null}
 */
export function readChatScrollPersist(key) {
  if (!key) return null
  const mem = memory.get(key)
  if (mem && Date.now() - mem.savedAt <= MAX_AGE_MS) {
    return { scrollTop: mem.scrollTop, pinned: mem.pinned }
  }
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const data = JSON.parse(raw)
    if (!data || typeof data.savedAt !== 'number') return null
    if (Date.now() - data.savedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(key)
      return null
    }
    const state = {
      scrollTop: Number(data.scrollTop) || 0,
      pinned: Boolean(data.pinned),
      savedAt: data.savedAt,
    }
    memory.set(key, state)
    return { scrollTop: state.scrollTop, pinned: state.pinned }
  } catch {
    return null
  }
}

/**
 * @param {string} key
 * @param {{ scrollTop: number, pinned: boolean }} state
 */
export function writeChatScrollPersist(key, state) {
  if (!key) return
  const row = {
    scrollTop: state.scrollTop,
    pinned: state.pinned,
    savedAt: Date.now(),
  }
  memory.set(key, row)
  try {
    sessionStorage.setItem(key, JSON.stringify(row))
  } catch { /* quota / private mode */ }
}

export function clearChatScrollPersist(key) {
  if (!key) return
  memory.delete(key)
  try { sessionStorage.removeItem(key) } catch { /* ignore */ }
}

/**
 * @param {string} key
 * @param {() => HTMLElement | null} getListEl
 */
export function registerChatScrollList(key, getListEl) {
  if (!key) return
  liveLists.set(key, getListEl)
}

export function unregisterChatScrollList(key) {
  if (!key) return
  liveLists.delete(key)
}

export function snapshotChatScrollList(key, listEl) {
  if (!key || !listEl) return
  writeChatScrollPersist(key, {
    scrollTop: listEl.scrollTop,
    pinned: isNearBottom(listEl),
  })
}

export function snapshotAllChatScrolls() {
  liveLists.forEach((getList, key) => {
    const list = getList()
    if (list) snapshotChatScrollList(key, list)
  })
}

/**
 * @param {string} key
 * @param {HTMLElement} list
 * @returns {boolean} true if a restore was applied
 */
export function restoreChatScrollList(key, list) {
  if (!key || !list) return false
  const saved = readChatScrollPersist(key)
  if (!saved) return false

  const apply = () => {
    list.style.scrollBehavior = 'auto'
    if (saved.pinned) {
      list.scrollTop = list.scrollHeight
    } else {
      const maxTop = Math.max(0, list.scrollHeight - list.clientHeight)
      list.scrollTop = Math.min(saved.scrollTop, maxTop)
    }
    list.style.scrollBehavior = ''
  }

  markScrollRestoreWindow()
  apply()
  requestAnimationFrame(() => {
    apply()
    requestAnimationFrame(() => {
      apply()
      markScrollRestoreWindow()
    })
  })
  return true
}

export function restoreAllChatScrolls() {
  markScrollRestoreWindow()
  liveLists.forEach((getList, key) => {
    const list = getList()
    if (list) restoreChatScrollList(key, list)
  })
}

let suppressSideEffectsUntil = 0

export function markScrollRestoreWindow(ms = 900) {
  suppressSideEffectsUntil = Date.now() + ms
}

export function scrollSideEffectsSuppressed() {
  return Date.now() < suppressSideEffectsUntil
}

let turboHooksInstalled = false

/** Call once from application entry (idempotent). */
export function installChatScrollTurboHooks() {
  if (turboHooksInstalled) return
  turboHooksInstalled = true

  const snap = () => snapshotAllChatScrolls()
  const restore = () => {
    requestAnimationFrame(() => {
      restoreAllChatScrolls()
      requestAnimationFrame(restoreAllChatScrolls)
    })
  }

  document.addEventListener('turbo:before-visit', snap)
  document.addEventListener('turbo:before-render', snap)
  window.addEventListener('pagehide', snap)
  document.addEventListener('turbo:render', restore)
  document.addEventListener('turbo:load', restore)
}
